import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, Crown } from "lucide-react";
import api from "@/api";
import { useI18n } from "@/i18n";
import { useMeta } from "@/contexts/MetaContext";
import { useAuth } from "@/contexts/AuthContext";

export default function Pricing() {
  const { t, lang } = useI18n();
  const navigate = useNavigate();
  const { me, loaded: meLoaded } = useAuth();
  const loadingMe = !meLoaded;
  const [plan, setPlan] = useState("annual"); // §19: annual pre-selected
  // The free cap lives server-side (single source of truth) — never hardcoded here.
  const meta = useMeta();
  const freeLimit = meta?.free_list_limit ?? 1;



  const premium = !!me && (me.premium_access);

  // Consent (waiver) and promo code are collected at the payment step only, for an identified user.
  const subscribe = () => {
    const target = `/checkout?plan=${plan}`;
    navigate(me ? target : `/auth?next=${encodeURIComponent(target)}`);
  };

  const Feature = ({ text }) => (
    <li className="flex items-start gap-2 text-sm text-slate-300">
      <Check className="mt-0.5 h-4 w-4 shrink-0 text-[#00FF87]" /> {text}
    </li>
  );

  const PlanOption = ({ value, price, per, badge }) => (
    <button
      type="button"
      onClick={() => setPlan(value)}
      data-testid={`plan-option-${value}`}
      className={`flex-1 rounded-lg border px-3 py-2.5 text-left transition-colors ${
        plan === value ? "border-[#FFD600] bg-[#FFD600]/10" : "border-[#1F293D] hover:border-[#FFD600]/40"
      }`}
    >
      <span className="flex items-center justify-between font-mono2 text-[10px] font-bold uppercase tracking-wider text-slate-300">
        {t(`premium.plan_${value}`)}
        {badge && <span className="rounded bg-[#00FF87]/15 px-1.5 py-0.5 text-[9px] text-[#00FF87]">{badge}</span>}
      </span>
      <span className="mt-0.5 block font-mono2 text-base font-bold text-slate-100">
        {price} <span className="text-[10px] font-normal text-slate-500">{per}</span>
      </span>
    </button>
  );

  return (
    <div className="pt-10" data-testid="pricing-page">
      <h1 className="text-center font-display text-4xl font-black uppercase tracking-tight text-white sm:text-5xl">{t("premium.title")}</h1>
      <div className="mx-auto mt-10 grid max-w-3xl grid-cols-1 gap-6 md:grid-cols-2">
        <div className="hud-card rounded-xl p-6">
          <h2 className="font-display text-2xl font-bold uppercase text-slate-100">{t("premium.free_name")}</h2>
          <p className="mt-1 font-mono2 text-3xl font-bold text-slate-400">0 €</p>
          <ul className="mt-5 space-y-2.5" data-testid="pricing-free-features">
            {["free_f1", "free_f2", "free_f3", "free_f4", "free_f5", "free_f6", "free_f7", "free_f8"].map((k) => (
              <Feature key={k} text={t(`premium.${k}`)} />
            ))}
            <Feature text={t("premium.free_f9", { n: freeLimit })} />
          </ul>
        </div>
        <div className="relative rounded-xl border border-[#FFD600]/60 bg-[#141A26] p-6 shadow-[0_0_30px_rgba(255,214,0,0.12)]">
          <span className="absolute -top-3 left-6 rounded-full bg-[#FFD600] px-2.5 py-0.5 font-mono2 text-[10px] font-bold uppercase tracking-wider text-[#0B0E14]">
            <Crown className="mr-1 inline h-3 w-3" /> Premium
          </span>
          <h2 className="font-display text-2xl font-bold uppercase text-slate-100">{t("premium.title")}</h2>
          <div className="mt-3 flex gap-2" data-testid="plan-selector">
            <PlanOption value="monthly" price={t("premium.monthly_price")} per={t("premium.per_month")} />
            <PlanOption value="annual" price={t("premium.annual_price")} per={t("premium.per_year")} badge={t("premium.annual_saving")} />
          </div>
          <p className="mt-1.5 font-mono2 text-[9px] uppercase tracking-wider text-slate-500">{t("premium.vat_included")}</p>
          <ul className="mt-4 space-y-2.5" data-testid="pricing-premium-features">
            {["pro_f1", "pro_f2", "pro_f3", "pro_f4", "pro_f5", "pro_f6"].map((k) => (
              <Feature key={k} text={t(`premium.${k}`)} />
            ))}
          </ul>
          {premium ? (
            <div className="mt-6 rounded-md border border-[#00FF87]/40 bg-[#00FF87]/10 px-3 py-2 text-center" data-testid="premium-current-plan">
              <p className="font-mono2 text-xs font-bold uppercase tracking-wider text-[#00FF87]">
                {me.plan === "premium" || me.role === "admin"
                  ? t("premium.current_plan")
                  : me.contributor_until
                    ? t("premium.access_active_until", { date: new Date(me.contributor_until).toLocaleDateString(lang === "fr" ? "fr-FR" : "en-GB") })
                    : t("premium.access_active")}
              </p>
              {me.plan !== "premium" && me.role !== "admin" && (
                <p className="mt-1 font-mono2 text-[10px] text-slate-400" data-testid="access-active-note">{t("premium.access_active_note")}</p>
              )}
            </div>
          ) : (
            <>
              <button data-testid="pricing-subscribe-button" onClick={subscribe} disabled={loadingMe} className="btn-volt mt-6 w-full disabled:opacity-50">
                {loadingMe ? t("common.loading") : me ? t("premium.subscribe") : t("premium.login_continue")}
              </button>
              <p className="mt-2 text-center font-mono2 text-[9px] uppercase tracking-wider text-slate-500" data-testid="pricing-consent-note">{t("premium.consent_at_payment")}</p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
